import dayjs from 'dayjs'
import { useMemo } from 'react'
import { Controller, FormProvider, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { usePatientIdentifierLabel } from '@/hooks/usePatientIdentifierLabel'

import { Button, Grid, Stack, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import PhoneNumberInputController from '@/components/ui/inputs/PhoneNumberInputController'
import { Patient } from '@/core/models/patient.model'
import TextInputController from '@/components/ui/inputs/TextInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import TextInput from '@/components/ui/inputs/TextInput'
import { useIsUS } from '@/hooks/useIsUS'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { calculateAge } from '@/utils/patient'

import { PatientInfoFormData, usePatientInfoForm } from '../model/usePatientInfoForm'

// TODO: extract to feature diagnostic patient info form
export function PatientInfoForm({ patient, readOnly }: { patient: Patient; readOnly?: boolean }) {
  const { t } = useTranslation()
  const identifierLabel = usePatientIdentifierLabel()
  const isTooSmall = useIsMobile()
  const isUS = useIsUS()
  const { rhForm } = usePatientInfoForm({ patient })

  const xs = isTooSmall ? 12 : 6

  const genderOptions = useMemo(
    () => [
      { label: t('field.gender.options.male.label'), value: 'M' },
      { label: t('field.gender.options.female.label'), value: 'F' },
      { label: t('field.gender.options.unknown.label'), value: 'I' },
    ],
    [t]
  )

  return (
    <FormProvider {...rhForm}>
      <form>
        <Grid container rowSpacing={3} columnSpacing={isTooSmall ? 2 : 4}>
          <Grid item xs={12}>
            <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={identifierLabel} name={isUS ? 'primaryPolicy' : 'secu'} />
          </Grid>
          <Grid item xs={xs}>
            <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.lastName.label')} name="surname" />
          </Grid>
          <Grid item xs={xs}>
            <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.firstName.label')} name="name" />
          </Grid>
          {isUS && (
            <Grid item xs={xs}>
              <TextInputController
                control={rhForm.control}
                readOnly={readOnly}
                fullWidth
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <span>{t('field.insured.label')}</span>
                    <Tooltip placement="top" title={t`(if different from the patient name)`}>
                      <InfoOutlinedIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                }
                name="insured"
              />
            </Grid>
          )}
          <Grid item xs={xs}>
            <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.email.label')} name="email" />
          </Grid>
          <Grid item xs={isTooSmall ? 5 : 6}>
            <SelectInputController
              control={rhForm.control}
              readOnly={readOnly}
              fullWidth
              label={t('field.gender.label')}
              name="gender"
              options={genderOptions}
            />
          </Grid>
          <Grid item xs={isTooSmall ? 7 : 6}>
            <Controller
              control={rhForm.control}
              name="birthday"
              render={({ field }) => (
                <DatePickerInput
                  disableFuture
                  fullWidth
                  label={t('field.birthday.label')}
                  onChange={(date) => {
                    rhForm.setValue('birthday', date?.toDate() ?? null)
                  }}
                  readOnly={readOnly}
                  value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              control={rhForm.control}
              name="birthday"
              render={({ field }) => (
                <TextInput fullWidth label={t('field.age.label')} name="age" value={field.value ? calculateAge(field.value) : ''} readOnly />
              )}
            />
          </Grid>
          <Grid item xs={xs}>
            <PhoneNumberInputController control={rhForm.control} readOnly={readOnly} label={t('field.phone.label')} name="phone" />
          </Grid>
          <Grid item xs={xs}>
            {isUS ? (
              <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.insuranceProvider.label')} name="insuranceProvider" />
            ) : (
              <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.codeGestion.label')} name="codeGestion" />
            )}
          </Grid>
          <Grid item xs={xs}>
            {isUS ? (
              <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.primaryInsurance.label')} name="primaryInsurance" />
            ) : (
              <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.codeRegime.label')} name="codeRegime" />
            )}
          </Grid>
          <Grid item xs={xs}>
            {isUS ? (
              <TextInputController
                control={rhForm.control}
                readOnly={readOnly}
                fullWidth
                label={t('field.insuredRelationship.label')}
                name="insuredRelationship"
              />
            ) : (
              <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.caisse.label')} name="caisse" />
            )}
          </Grid>
          {isUS && (
            <Grid item xs={xs}>
              <TextInputController
                control={rhForm.control}
                readOnly={readOnly}
                fullWidth
                label={t('field.secondaryInsurance.label')}
                name="secondaryInsurance"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <NotesField readOnly={readOnly} />
          </Grid>
          <Grid item xs={12}>
            <TextInputController
              control={rhForm.control}
              readOnly={readOnly}
              fullWidth
              label={t('field.treatment.label', { count: 2 })}
              multiline
              name="treatment"
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputController
              control={rhForm.control}
              readOnly={readOnly}
              fullWidth
              label={t('field.background.label', { count: 2 })}
              multiline
              name="background"
            />
          </Grid>
          {isUS && (
            <Grid item xs={12}>
              <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.primaryPharmacy.label')} name="primaryPharmacy" />
            </Grid>
          )}
        </Grid>
      </form>
    </FormProvider>
  )
}

function NotesField({ readOnly }: { readOnly?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<PatientInfoFormData>()
  const notes = useWatch({ control: rhForm.control, name: 'notes' })
  return (
    <>
      <TextInputController control={rhForm.control} readOnly={readOnly} fullWidth label={t('field.note.label', { count: 2 })} multiline name="notes" />
      {notes && !readOnly && (
        <Button type="submit" size="small" variant="contained" sx={{ mt: 1 }}>
          {t('common:button.save.label')}
        </Button>
      )}
    </>
  )
}
